import { rewardsOverviewText } from "../../settings/Constants";
import Banner from "./Banner";
import Card from "./Card";
import PrevArrow from "../UI/Icons/PrevArrow";
import NextArrow from "../UI/Icons/NextArrow";
import Line from "./Line";
import Footer from "./Footer";
import Carousel from "react-bootstrap/Carousel";
import styled, { css } from "styled-components";
import { rem } from "polished";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

const StyledCarousel = styled(Carousel)`
  .carousel-control-next,
  .carousel-control-prev {
    width: 2%;
  }
  .carousel-control-next {
    right: -10px;
  }
  .carousel-control-prev {
    left: -10px;
  }

  .carousel-control-next svg,
  .carousel-control-prev svg {
    width: ${rem("18px")};
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .sr-only-focusable:active,
  .sr-only-focusable:focus {
    position: static;
    width: auto;
    height: auto;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }
`;

const CardContainer = styled.div`
  margin: ${rem("40px")} ${rem("0px")} ${rem("16px")};
  padding: ${rem("0px")} ${rem("10px")};
  display: flex;
  justify-content: space-evenly;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  align-content: center;
  ${({ hasDescription }) => (hasDescription ?
    "" : "justify-content: center"
  )}
`;

const IconContainer = styled.div`
  align-self: center;

  > img {
    width: auto;
    max-width: 250px;
    max-height: ${rem("50px")};
    ${({ hasDescription }) => (hasDescription ?
    `
      margin-top: 48px;
      margin-bottom: 16px;
    ` : ""
    )}
  }
`;

const CardText = styled.div`
  text-align: center;
  max-width: ${rem("220px")};
  font-size: ${rem("14px")};
  font-weight: 400;
  line-height: ${rem("20px")};
  color: #666666;
  overflow: hidden;
`;

const Disclaimer = styled.div`
  max-width: ${rem("674px")};
  font-size: ${rem("12px")};
  line-height: ${rem("20px")};
  text-align: center;
  color: #666666;
  margin: ${rem("16px")} auto ${rem("0px")};

  > a {
    text-decoration: underline;
    color: #666666;

    &:hover {
      color: #666666;
    }
  }

  ${({ darkMode }) =>
    darkMode === "true" &&
    css`
      color: #ffffff;

      > a {
        text-decoration: underline;
        color: #ffffff;

        &:hover {
          color: #ffffff;
        }
      }
    `}
`;

export const CashBackCarouselOptions = ({ redemptionArray, darkMode }) => {
  return (
    <StyledCarousel
      indicators={false}
      prevIcon={<PrevArrow darkMode={darkMode} />}
      nextIcon={<NextArrow darkMode={darkMode} />}
      interval={100000}
    >
      {redemptionArray.map((redemptionGroup, key) => (
        <Carousel.Item key={key}>
          <CardContainer>
            {redemptionGroup.map((redemptionItem) => (
              <Card key={redemptionItem.id}>
                <CardContent
                  hasDescription={!!redemptionItem.description}
                >
                  <IconContainer
                    hasDescription={!!redemptionItem.description}
                  >
                    <img
                      alt={redemptionItem.merchant_name}
                      src={"https://" + redemptionItem.image_url}
                    />
                  </IconContainer>
                  {redemptionItem.description &&
                    <CardText>
                      {redemptionItem.description
                        .replace(/<\/span>|<p>|<\/p>|<br>|&nbsp;/g, "")
                        .replace(/<span style="font-size: 1rem;">/g, " ")
                        .replace("&amp;", "&")}
                    </CardText>
                  }
                </CardContent>
              </Card>
            ))}
          </CardContainer>
        </Carousel.Item>
      ))}
    </StyledCarousel>
  );
};

const CashBack = ({
  widgetIdentifier,
  redemptionArray,
  coloredTiles,
  primaryColor,
  darkMode,
}) => {
  const [redemptions, setRedemptions] = useState([]);

  // Redemption options lack unique identifiers — duplicates appear
  // under the same umbrella (e.g., PayPal vs PayPal International).
  // Deduplicate by merchant name and logo URL as a heuristic.
  useEffect(() => {
    const seenMerchant = new Set();
    const seenImage = new Set();
    const uniqueRedemptions = [];

    redemptionArray.flat().forEach((redemption) => {
      if (!(
        seenMerchant.has(redemption.merchant_name) || 
        seenImage.has(redemption.image_url) || 
        seenImage.has(redemption.image_url2)
      )) {
        seenMerchant.add(redemption.merchant_name);
        if (redemption.image_url) seenImage.add(redemption.image_url);
        if (redemption.image_url2) seenImage.add(redemption.image_url2);

        uniqueRedemptions.push(redemption);
      }
    });

    const redemptionGroup = []
    for (let i = 0; i < uniqueRedemptions.length; i += 3) {
      redemptionGroup.push(uniqueRedemptions.slice(i, i + 3));
    }

    setRedemptions(redemptionGroup);
  }, []);

  return (
    <div>
      <Banner
        backgroundImg={
          widgetIdentifier.includes("yotel")
            ? rewardsOverviewText.cashbackSection.bannerImgYotel
            : rewardsOverviewText.cashbackSection.bannerImg
        }
        title={rewardsOverviewText.cashbackSection.title}
        subtitle={rewardsOverviewText.nonInstayCards[0].description}
        coloredTiles={coloredTiles}
        primaryColor={primaryColor}
      />
      <CashBackCarouselOptions
        redemptionArray={redemptions}
        darkMode={darkMode}
      />
      <Disclaimer darkMode={darkMode}>
        <a href="https://theguestbook.com/faq#Rewards" target="_blank">
          {rewardsOverviewText.learnMore}
        </a>
      </Disclaimer>
      <Line />
      <Footer widgetIdentifier={widgetIdentifier} darkMode={darkMode} />
    </div>
  );
};

CashBack.propTypes = {
  widgetIdentifier: PropTypes.string.isRequired,
  redemptionArray: PropTypes.array.isRequired,
  coloredTiles: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  primaryColor: PropTypes.string,
  darkMode: PropTypes.string.isRequired,
};

export default CashBack;
